/* =============================================================================
   CONSENTIMIENTO DE COOKIES
   -----------------------------------------------------------------------------
   Punto único desde el que se cargan los servicios de terceros que ponen
   cookies o envían datos fuera: Google Analytics 4 y Microsoft Clarity.

   LA REGLA: aquí no se carga NADA hasta que el usuario pulsa "Aceptar" en el
   banner. Antes de eso el navegador del visitante no hace ni una petición a
   Google ni a Microsoft, así que ni su IP sale de la web. Es lo que exigen el
   RGPD y la LSSI: el consentimiento tiene que ser previo, y un banner que
   avisa mientras el script ya está midiendo no vale.

   Además, Clarity exige desde el 31 de octubre de 2025 una señal explícita de
   consentimiento para visitantes del EEE, Reino Unido y Suiza. Se la mandamos
   con la API consentv2 en cuanto carga.

   La decisión se guarda en localStorage bajo 'gr-cookie-consent'. Si el
   usuario rechaza (o cambia de idea y rechaza después de haber aceptado),
   borramos las cookies que ya hubiera puesto Google o Microsoft.

   Si un ID de SITE.analitica está vacío, ese servicio no se carga nunca, ni
   aunque el usuario acepte. Sirve para desactivar uno de los dos sin tocar
   código.

   USO
     import consentimiento from '@/plugins/consentimiento';
     consentimiento.aplicar(true);            // el usuario acepta
     consentimiento.aplicar(false);           // el usuario rechaza
     consentimiento.leer();                   // 'accepted' | 'rejected' | null
     consentimiento.aplicarSiYaDecidido();    // al cargar la web
     consentimiento.olvidar();                // volver a preguntar
   ============================================================================= */

import site from '@/content/site';

var SITE = site.SITE || site;

var CLAVE = 'gr-cookie-consent';

/* Cookies que hay que barrer si el usuario rechaza después de haber aceptado.
   Se comparan por prefijo: _ga, _ga_XXXX, _clck, _clsk... */
var PREFIJOS_A_BORRAR = ['_ga', '_gid', '_gat', '_clck', '_clsk', 'CLID', 'MUID', 'ANONCHK', 'SM'];

function idGa4() {
  return (SITE && SITE.analitica && SITE.analitica.ga4Id) || '';
}

function idClarity() {
  return (SITE && SITE.analitica && SITE.analitica.clarityProjectId) || '';
}

/* -----------------------------------------------------------------------------
   Lectura y escritura de la decisión
   -------------------------------------------------------------------------- */

function leer() {
  try {
    var bruto = localStorage.getItem(CLAVE);
    if (!bruto) return null;
    var dato = JSON.parse(bruto);
    return (dato && dato.consent) || null;
  } catch (e) {
    /* Modo incógnito estricto o localStorage bloqueado: tratamos la visita
       como si no hubiera decidido nada. No se carga nada. */
    return null;
  }
}

function guardar(aceptado) {
  try {
    localStorage.setItem(CLAVE, JSON.stringify({
      consent: aceptado ? 'accepted' : 'rejected',
      date: new Date().toISOString()
    }));
  } catch (e) {
    /* Si no se puede guardar, el banner volverá a salir en la siguiente
       visita. Molesto pero correcto: nunca damos por hecho un "sí". */
  }
}

function olvidar() {
  try {
    localStorage.removeItem(CLAVE);
  } catch (e) {
    /* nada que hacer */
  }
}

/* -----------------------------------------------------------------------------
   Borrado de cookies de terceros
   -------------------------------------------------------------------------- */

function borrarCookie(nombre) {
  var host = window.location.hostname;
  /* El mismo nombre puede estar puesto en varios dominios y rutas. Probamos
     las combinaciones habituales: no hay forma de saber cuál usó el script. */
  var dominios = ['', host, '.' + host];
  var partes = host.split('.');
  if (partes.length > 2) {
    var raiz = partes.slice(-2).join('.');
    dominios.push(raiz, '.' + raiz);
  }
  var rutas = ['/', window.location.pathname];

  for (var d = 0; d < dominios.length; d++) {
    for (var r = 0; r < rutas.length; r++) {
      document.cookie = nombre + '=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=' +
        rutas[r] + (dominios[d] ? '; domain=' + dominios[d] : '');
    }
  }
}

function barrerCookiesDeTerceros() {
  var todas = (document.cookie || '').split(';');
  for (var i = 0; i < todas.length; i++) {
    var nombre = todas[i].split('=')[0].trim();
    if (!nombre) continue;
    for (var p = 0; p < PREFIJOS_A_BORRAR.length; p++) {
      if (nombre.indexOf(PREFIJOS_A_BORRAR[p]) === 0) {
        borrarCookie(nombre);
        break;
      }
    }
  }
}

/* -----------------------------------------------------------------------------
   Carga de los scripts
   -------------------------------------------------------------------------- */

var ga4Cargado = false;
var clarityCargado = false;

function cargarGa4() {
  var id = idGa4();
  if (!id || ga4Cargado) return;
  ga4Cargado = true;

  var s = document.createElement('script');
  s.async = true;
  s.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(id);
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  /* anonymize_ip es redundante en GA4 (ya la trunca siempre), pero dejarlo
     explícito documenta la intención si alguien audita la web. */
  window.gtag('config', id, { anonymize_ip: true });
}

function cargarClarity() {
  var id = idClarity();
  if (!id || clarityCargado) return;
  clarityCargado = true;

  /* Snippet oficial de Clarity, escrito sin eval ni document.write. */
  window.clarity = window.clarity || function () {
    (window.clarity.q = window.clarity.q || []).push(arguments);
  };

  var s = document.createElement('script');
  s.async = true;
  s.src = 'https://www.clarity.ms/tag/' + encodeURIComponent(id);
  document.head.appendChild(s);

  /* Señal de consentimiento. Sin esto, para visitantes del EEE Clarity entra
     en "no-consent mode" y ni siquiera enlaza las páginas de una misma
     sesión. Publicidad denegada: no hacemos remarketing con Clarity. */
  window.clarity('consentv2', {
    ad_Storage: 'denied',
    analytics_Storage: 'granted'
  });
}

function retirarClarity() {
  if (!window.clarity) return;
  try {
    /* Cierra la sesión, borra las cookies de Clarity y sigue en no-consent. */
    window.clarity('consent', false);
  } catch (e) {
    /* si el script no llegó a cargar, no hay nada que retirar */
  }
}

/* -----------------------------------------------------------------------------
   API pública
   -------------------------------------------------------------------------- */

function aplicar(aceptado, guardarDecision) {
  if (guardarDecision !== false) guardar(aceptado);

  if (aceptado) {
    cargarGa4();
    cargarClarity();
    return;
  }

  /* Rechazo: no cargamos nada y limpiamos lo que hubiera de una visita
     anterior en la que sí aceptó. */
  retirarClarity();
  barrerCookiesDeTerceros();
}

function aplicarSiYaDecidido() {
  if (leer() === 'accepted') aplicar(true, false);
}

export default {
  CLAVE: CLAVE,
  leer: leer,
  olvidar: olvidar,
  aplicar: aplicar,
  aplicarSiYaDecidido: aplicarSiYaDecidido
};
